import React from "react";
import { Box, Skeleton } from "@mui/material";
import { useParams } from "react-router-dom";
import PageContainer from "../PageContainer";
import AwardSkeleton, { NewAwardSkeleton } from "./AwardSkeleton";
import CertificateCardsSkeleton from "./CertificateCardsSkeleton";
import CertificateTemplateSkeleton from "./CertificateTemplateSkeleton";
import {
  AwardsHeader,
  CertificateTemplatesHeader,
  CertificatesHomeHeader,
} from "./CertificatesHeaders";
import { CERTIFICATES_TILE_GRID_SX } from "./certificateStyles";
import {
  builtInCertificateTemplates,
  findCertificateTemplate,
} from "../../domain/certificates/certificateTemplates";
import { awardTabFromAddress } from "../../routes";

/*
 * Each Certificates page while its code downloads. They match what the page
 * itself shows while its data loads, so the two read as one wait.
 */

export const CertificatesHomeSkeleton: React.FC = () => (
  <PageContainer>
    <CertificatesHomeHeader />
    <Box role="status" aria-label="Loading Certificates" sx={CERTIFICATES_TILE_GRID_SX}>
      {[0, 1].map((tile) => (
        <Skeleton key={tile} variant="rounded" height={296} />
      ))}
    </Box>
  </PageContainer>
);

export const CertificateTemplatesSkeleton: React.FC = () => (
  <PageContainer>
    <CertificateTemplatesHeader />
    <CertificateCardsSkeleton label="Loading certificate templates" />
  </PageContainer>
);

export const AwardsSkeleton: React.FC = () => (
  <PageContainer>
    <AwardsHeader />
    <CertificateCardsSkeleton label="Loading Awards" />
  </PageContainer>
);

export const AwardRouteSkeleton: React.FC = () => {
  const { tab } = useParams<{ tab?: string }>();
  return (
    <PageContainer>
      <AwardSkeleton tab={awardTabFromAddress(tab)} />
    </PageContainer>
  );
};

export const NewAwardRouteSkeleton: React.FC = () => (
  <PageContainer>
    <NewAwardSkeleton />
  </PageContainer>
);

export const CertificateTemplateRouteSkeleton: React.FC = () => {
  const { certificateTemplateId = "" } = useParams<{ certificateTemplateId: string }>();
  // A ready-made one is locked, so it opens without the settings column.
  const builtInTemplate = findCertificateTemplate(
    builtInCertificateTemplates({}),
    certificateTemplateId
  );
  return (
    <PageContainer>
      <CertificateTemplateSkeleton
        title={builtInTemplate?.name ?? "Certificate template"}
        withSettings={!builtInTemplate}
      />
    </PageContainer>
  );
};

export const NewCertificateTemplateRouteSkeleton: React.FC = () => (
  <PageContainer>
    <CertificateTemplateSkeleton title="New certificate template" withSettings />
  </PageContainer>
);
